import { Button } from '@mui/material';
import { validateNotNullOrUndefined } from '@weft/utils';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { useCreateInvoiceContext } from '../create-invoice-page-contexts/create-invoice-context/create-invoice-context';
import type { InvoiceCreationStep } from '../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/invoice-creation-step';
import { INVOICE_CREATION_STEPS } from '../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/invoice-creation-step';
import { CREATE_INVOICE_DEFAULT_VALUES } from './CreateInvoiceForm/create-invoice-form-consts/create-invoice-default-values';
import type { InvoiceFormInput } from './CreateInvoiceForm/create-invoice-form-types/invoice-form-input';

const INVOICE_CREATION_STEPS_CTA_LABELS: Record<InvoiceCreationStep, React.ReactNode> = {
  [INVOICE_CREATION_STEPS.settings]: 'Save Settings & Continue',
  [INVOICE_CREATION_STEPS.content]: 'Confirm & Create Invoice',
};

export const CreateInvoicePageStepperNavigator: React.FC = () => {
  const { invoiceCreationStepper: { currentStep, nextStep, onFinalStep, isFinalStep, goToStep } } = useCreateInvoiceContext();
  const { setShowErrorNotification } = useFormErrorNotificationContext();
  const { trigger, reset } = useFormContext<InvoiceFormInput | { 'productsValidation': unknown }>();
  const navigate = useVyNavigate();

  validateNotNullOrUndefined(onFinalStep);

  const handleClick = useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    let validForm = false;

    if (currentStep === INVOICE_CREATION_STEPS.content) {
      validForm = await trigger(['fixedProducts', 'usageProducts', 'productsValidation']);
    } else if (currentStep === INVOICE_CREATION_STEPS.settings) {
      validForm = await trigger(['settings']);
    }
      
    if (!validForm) {
      setShowErrorNotification(true);

      return undefined;
    }

    if (isFinalStep) {
      return onFinalStep();
    }

    event.preventDefault();

    return nextStep();
  }, [currentStep, isFinalStep, nextStep, onFinalStep, setShowErrorNotification, trigger]);

  const handleClearForm = useCallback(() => {
    reset(CREATE_INVOICE_DEFAULT_VALUES, {
      keepDefaultValues: true,
    });

    goToStep(0);
    navigate('/invoices/create');
  }, [goToStep, navigate, reset]);

  return (
    <div className="flex justify-end gap-x-2 border-t border-t-[#DFEBF1] bg-white p-4">
      <Button
        variant="text"
        color="primary"
        size="large"
        disableElevation
        className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
        onClick={handleClearForm}
      >
        Clear form
      </Button>

      <Button
        variant="contained"
        color="primary"
        size="medium"
        disableElevation
        className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
        onClick={handleClick}
        type={isFinalStep ? 'submit' : 'button'}
      >
        {INVOICE_CREATION_STEPS_CTA_LABELS[currentStep]}
      </Button>
    </div>
  );
};